import React, { useState, useCallback, useEffect } from 'react';
import {
  Button, Card, DatePicker, Form, Input, InputNumber, Modal, Spin, Table, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type {
  FP230, FP231, FP231Input, CreateP203Request,
} from '../../services/p203Api';
import p203Api from '../../services/p203Api';
import LookupInput from '../../components/LookupInput';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { useModulePermission } from '../../hooks/usePermission';
import { BatchAcceptanceBridge, pwp203f1AcceptanceManifest } from '../../features/acceptance';

type Mode = 'browse' | 'add' | 'edit';

// Maps F11A_15 (output format code) to column-header labels
const OUTPUT_FORMAT_LABELS: Record<number, string[]> = {
  1: ['CHANGE', 'COT', 'NTL', 'EOT'],   // color fastness
  2: ['DRY', 'WET'],                       // rubbing
  3: ['WARP', 'WEFT'],                     // dimensional
  4: ['BEFORE WASH', '3WASH'],             // washing
  5: ['單一結果'],                          // single value
};

function getFormatLabel(f11a15: number | undefined): string {
  if (!f11a15) return '';
  const labels = OUTPUT_FORMAT_LABELS[f11a15];
  return labels ? labels.join(' / ') : '';
}

const emptyForm = (): Partial<CreateP203Request> => ({
  fp230_11: null,
  fp230_12: null,
  fp230_121: '',
  fp230_13: '',
  fp230_15: undefined,
  fp230_17: '',
  details: [],
});

const P203Form: React.FC = () => {
  const navigate = useNavigate();
  const perm = useModulePermission('pwp203f1');
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [form] = Form.useForm();
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FP230[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [details, setDetails] = useState<FP231Input[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [total, setTotal] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const current = currentIdx >= 0 ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load list
  const loadList = useCallback(async (kw?: string) => {
    setLoading(true);
    try {
      const res = await p203Api.list(1, 200, kw);
      setLastApiResponse(res);
      setRecords(res.data);
      setTotal(res.total);
      if (res.data.length > 0) {
        setCurrentIdx(0);
        populateForm(res.data[0]);
      } else {
        setCurrentIdx(-1);
        form.resetFields();
        setDetails([]);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadList().then(() => {
      // reviewId handling is done below in a separate effect after records are populated
    });
  }, [loadList]);

  // If ?review= is present, jump to that record once records are loaded
  useEffect(() => {
    if (reviewId && records.length > 0) {
      const idx = records.findIndex(r => r.fp230_01 === reviewId);
      if (idx >= 0) {
        setCurrentIdx(idx);
        populateForm(records[idx]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, records.length]);

  // Populate form from record
  function populateForm(rec: FP230) {
    form.setFieldsValue({
      fp230_11: rec.fp230_11 ? dayjs(rec.fp230_11) : null,
      fp230_12: rec.fp230_12 ? dayjs(rec.fp230_12) : null,
      fp230_121: rec.fp230_121,
      fp230_13: rec.fp230_13,
      fp230_15: rec.fp230_15,
      fp230_17: rec.fp230_17,
    });
    setDetails((rec.details || []).map((d: FP231) => ({
      fp231_02: d.fp231_02,
      fp231_11: d.fp231_11,
      fp231_12: d.fp231_12,
      fp231_13: d.fp231_13,
      fp231_14: d.fp231_14,
      fp231_15: d.fp231_15,
      fp231_112: d.fp231_112,
      fp231_121: d.fp231_121,
      fp231_128: d.fp231_128,
      f11a_15: d.f11a_15,
      f11a_11: d.f11a_11,
    })));
  }

  // Navigation
  const navFirst = () => { if (records.length > 0) { setCurrentIdx(0); populateForm(records[0]); } };
  const navPrev = () => {
    if (currentIdx > 0) { setCurrentIdx(currentIdx - 1); populateForm(records[currentIdx - 1]); }
  };
  const navNext = () => {
    if (currentIdx < records.length - 1) { setCurrentIdx(currentIdx + 1); populateForm(records[currentIdx + 1]); }
  };
  const navLast = () => {
    if (records.length > 0) { const last = records.length - 1; setCurrentIdx(last); populateForm(records[last]); }
  };

  // CRUD handlers
  const handleAdd = () => {
    setMode('add');
    form.resetFields();
    form.setFieldsValue(emptyForm());
    setDetails([]);
  };

  const handleEdit = () => {
    if (!current) return;
    setMode('edit');
  };

  const handleCancel = () => {
    setMode('browse');
    if (current) populateForm(current);
    else { form.resetFields(); setDetails([]); }
  };

  const handleSave = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const req: CreateP203Request = {
      fp230_11: values.fp230_11 ? (values.fp230_11 as dayjs.Dayjs).toISOString() : null,
      fp230_12: values.fp230_12 ? (values.fp230_12 as dayjs.Dayjs).toISOString() : null,
      fp230_121: String(values.fp230_121 || ''),
      fp230_13: String(values.fp230_13 || ''),
      fp230_15: Number(values.fp230_15) || 0,
      fp230_17: String(values.fp230_17 || ''),
      details,
    };

    setLoading(true);
    try {
      if (mode === 'add') {
        const created = await p203Api.create(req);
        setLastApiResponse(created);
        message.success('新增成功');
      } else {
        const updated = await p203Api.update(current!.fp230_01, req);
        setLastApiResponse(updated);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadList(searchText || undefined);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { errors?: { message: string }[] } } };
      if (e?.response?.data?.errors) {
        const msgs = e.response.data.errors.map((x) => x.message).join('; ');
        message.error(msgs);
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除報告「${current.fp230_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        setLoading(true);
        try {
          await p203Api.delete(current.fp230_01);
          setLastApiResponse({ deleted: current.fp230_01 });
          message.success('刪除成功');
          await loadList(searchText || undefined);
        } catch {
          message.error('刪除失敗');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Copy handler — duplicate current record into add mode
  const handleCopy = () => {
    if (!current) return;
    setMode('add');
    form.setFieldsValue({
      fp230_11: current.fp230_11 ? dayjs(current.fp230_11) : null,
      fp230_12: current.fp230_12 ? dayjs(current.fp230_12) : null,
      fp230_121: '',
      fp230_13: current.fp230_13,
      fp230_15: current.fp230_15,
      fp230_17: current.fp230_17,
    });
    setDetails((current.details || []).map((d: FP231) => ({
      fp231_02: d.fp231_02,
      fp231_11: d.fp231_11,
      fp231_12: d.fp231_12,
      fp231_13: d.fp231_13,
      fp231_14: d.fp231_14,
      fp231_15: d.fp231_15,
      fp231_112: d.fp231_112,
      fp231_121: d.fp231_121,
      fp231_128: d.fp231_128,
      f11a_15: d.f11a_15,
      f11a_11: d.f11a_11,
    })));
  };

  // Print handler (placeholder — print form PWP203FP not yet implemented)
  const handlePrint = () => {
    if (!current) return;
    message.info(`列印報告 ${current.fp230_01}（列印功能尚未實作）`);
  };

  // Approval handlers
  const handleSubmit = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將報告 ${current.fp230_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p203Api.submitForApproval(current.fp230_01);
          setLastApiResponse(resp);
          message.success(resp.message || '送審成功');
          await loadList(searchText || undefined);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [current, isEditing, loadList, searchText]);

  const handleReject = useCallback(() => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回報告 ${current.fp230_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await p203Api.reject(current.fp230_01);
          setLastApiResponse(resp);
          message.success(resp.message || '退回成功');
          await loadList(searchText || undefined);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [current, isEditing, loadList, searchText]);

  // Detail table handlers
  const addDetailRow = () => {
    setDetails((prev) => [
      ...prev,
      { fp231_02: prev.length + 1, fp231_11: '', fp231_12: '', fp231_13: '', fp231_14: '', fp231_15: '', fp231_112: '', fp231_121: '', fp231_128: '' },
    ]);
  };

  const removeDetailRow = (idx: number) => {
    setDetails((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateDetailCell = (idx: number, field: keyof FP231Input, value: string) => {
    setDetails((prev) => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  // Detail columns
  const detailColumns: ColumnsType<FP231Input> = [
    {
      title: '項次', dataIndex: 'fp231_02', width: 55,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_02' } as React.HTMLAttributes<HTMLElement>),
      render: (_, __, idx) => idx + 1,
    },
    {
      title: '測試項目', dataIndex: 'fp231_11', width: 140,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_11' } as React.HTMLAttributes<HTMLElement>),
      render: (val, row, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_11', e.target.value)} />
        : (row.f11a_11 ? `${val} ${row.f11a_11}` : val),
    },
    {
      title: '輸出格式', dataIndex: 'f11a_15', width: 160,
      onHeaderCell: () => ({ 'data-contract-field': 'f11a_15' } as React.HTMLAttributes<HTMLElement>),
      render: (val: number | undefined) => (
        <span style={{ color: '#666', fontSize: 12 }}>{getFormatLabel(val)}</span>
      ),
    },
    {
      title: '測試方法', dataIndex: 'fp231_12', width: 130,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_12' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_12', e.target.value)} />
        : val,
    },
    {
      title: '退變色', dataIndex: 'fp231_121', width: 110,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_121' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_121', e.target.value)} />
        : val,
    },
    {
      title: '復布污染', dataIndex: 'fp231_128', width: 110,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_128' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_128', e.target.value)} />
        : val,
    },
    {
      title: '標準', dataIndex: 'fp231_13', width: 120,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_13' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_13', e.target.value)} />
        : val,
    },
    {
      title: '判定', dataIndex: 'fp231_14', width: 90,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_14' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_14', e.target.value.toUpperCase())} />
        : val,
    },
    {
      title: '廠商代號', dataIndex: 'fp231_15', width: 110,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_15' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" maxLength={12} value={val} onChange={(e) => updateDetailCell(idx, 'fp231_15', e.target.value)} />
        : val,
    },
    {
      title: '規格代號', dataIndex: 'fp231_112', width: 110,
      onHeaderCell: () => ({ 'data-contract-field': 'fp231_112' } as React.HTMLAttributes<HTMLElement>),
      render: (val, _, idx) => isEditing
        ? <Input size="small" value={val} onChange={(e) => updateDetailCell(idx, 'fp231_112', e.target.value)} />
        : val,
    },
    {
      title: '', width: 40,
      render: (_, __, idx) => isEditing
        ? <button className="sab-btn sab-btn-danger" style={{ padding: '2px 6px' }} onClick={() => removeDetailRow(idx)}><DeleteOutlined /></button>
        : null,
    },
  ];

  // Approval state
  // FP230_80 統一碼（批次 5）：N=草稿/退回/沖銷、D=送審、P=核准；'A'=組員複核（規格中間態）
  const s80 = (current?.fp230_80 || '').trim();
  const approval: ApprovalState = {
    statusCode: s80,
    isApproved: s80 === 'P',
    isPending: s80 === 'A',
    isLocked: ['P', 'A'].includes(s80),
  };
  const acceptanceSelectedKeys = current ? [current.fp230_01] : [];
  const queryAcceptanceExact = useCallback((id: string) => p203Api.getByID(id), []);

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const recordCount = records.length > 0
    ? `${currentIdx + 1} / ${records.length} (共 ${total})`
    : '無資料';

  return (
    <Spin spinning={loading}>
      <Card
        title={`物化性測試報告 — ${modeLabel}`}
        extra={<span style={{ fontSize: 12, color: '#888' }}>{recordCount}</span>}
        style={{ margin: 16 }}
      >
        {/* Search bar */}
        <div style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder="搜尋報告編號/送件單號/檢驗人員"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(val) => loadList(val || undefined)}
            style={{ width: 300 }}
            disabled={isEditing}
          />
        </div>

        {/* Master form */}
        <Form form={form} layout="inline" style={{ marginBottom: 12, flexWrap: 'wrap', gap: '8px 0' }}>
          <Form.Item label="報告單號">
            <Input value={current?.fp230_01 || '(自動產生)'} disabled style={{ width: 120 }} data-contract-field="fp230_01" />
          </Form.Item>
          <Form.Item label="輸入日期" name="fp230_11">
            <DatePicker disabled style={{ width: 130 }} />
          </Form.Item>
          <Form.Item label="完成日期" name="fp230_12">
            <DatePicker disabled={!isEditing} style={{ width: 130 }} />
          </Form.Item>
          <Form.Item label="送件單號" name="fp230_13"
            rules={[{ required: true, message: '送件單號不可為空白' }]}>
            <LookupInput
              lookupType="inspectionOrder"
              contractFieldId="fp230_13"
              value={form.getFieldValue('fp230_13') || ''}
              onChange={(v) => form.setFieldsValue({ fp230_13: v })}
              onCodeConfirm={(v) => form.setFieldsValue({ fp230_13: v })}
              disabled={!isEditing}
              style={{ width: 100 }}
              maxLength={6}
            />
          </Form.Item>
          <Form.Item label="送件項次" name="fp230_15"
            rules={[{ required: true, message: '送件項次不可為空白' }]}>
            <InputNumber disabled={!isEditing} style={{ width: 80 }} min={1} max={99} precision={0} />
          </Form.Item>
          <Form.Item label="缸號">
            <Input value={current?.fp230_14 || ''} disabled style={{ width: 80 }} data-contract-field="fp230_14" />
          </Form.Item>
          <Form.Item label="顏色">
            <Input value={current?.fp230_16 || ''} disabled style={{ width: 80 }} data-contract-field="fp230_16" />
          </Form.Item>
          <Form.Item label="檢驗人員" name="fp230_17"
            rules={[{ required: true, message: '檢驗人員不可為空白' }]}>
            <LookupInput
              lookupType="employee"
              contractFieldId="fp230_17"
              value={form.getFieldValue('fp230_17') || ''}
              onChange={(v) => form.setFieldsValue({ fp230_17: v })}
              onCodeConfirm={(v) => form.setFieldsValue({ fp230_17: v })}
              disabled={!isEditing}
              style={{ width: 100 }}
              maxLength={8}
            />
          </Form.Item>
          <Form.Item label="核准狀態">
            <Input value={current?.fp230_80 || ''} disabled style={{ width: 60 }} data-contract-field="fp230_80" />
          </Form.Item>
        </Form>

        {/* Detail table */}
        <Card
          size="small"
          title="測試項目明細"
          extra={isEditing && (
            <button className="sab-btn" onClick={addDetailRow}><PlusOutlined /> 新增明細</button>
          )}
        >
          <Table
            dataSource={details}
            columns={detailColumns}
            rowKey={(_, idx) => String(idx)}
            size="small"
            pagination={false}
            scroll={{ x: 650 }}
          />
        </Card>

        {/* SharedActionBar */}
        <div style={{ marginTop: 12 }}>
          <SharedActionBar
            acceptanceActionIds={{
              list: 'PWP203-ACT-READ',
              create: 'PWP203-ACT-CREATE',
              update: 'PWP203-ACT-UPDATE',
              delete: 'PWP203-ACT-DELETE',
            }}
            mode={mode} isEditing={isEditing} hasRecord={!!current}
            currentIdx={currentIdx} recordCount={records.length}
            onNavFirst={navFirst} onNavPrev={navPrev}
            onNavNext={navNext} onNavLast={navLast}
            onAdd={handleAdd} onModify={handleEdit} onDelete={handleDelete}
            onSave={handleSave} onCancel={handleCancel}
            onCopy={handleCopy} onPrint={handlePrint}
            approval={approval}
            extraBrowseButtons={s80 === 'N' || s80 === '' ? (
              <Button
                disabled={!current || !perm.canApprove}
                onClick={handleSubmit}
                data-testid="p203-review-submit"
                data-acceptance-action="PWP203-ACT-SUBMIT"
              >
                送交組員複核
              </Button>
            ) : s80 === 'A' ? (
              <Button
                danger
                disabled={!current || !perm.canApprove}
                onClick={handleReject}
                data-testid="p203-review-return"
                data-acceptance-action="PWP203-ACT-RETURN"
              >
                退回未核准
              </Button>
            ) : null}
            onHistory={() => setShowHistory(true)}
            canCreate={perm.canCreate} canEdit={perm.canEdit} canDelete={perm.canDelete} canApprove={perm.canApprove}
            onExit={() => navigate(-1)}
          />
        </div>

        <BatchAcceptanceBridge
          manifest={pwp203f1AcceptanceManifest}
          records={records}
          selectedKeys={acceptanceSelectedKeys}
          keyOf={(item) => item.fp230_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={lastApiResponse}
          loading={loading}
          onAutoFill={() => {
            if (records.length > 0) {
              setCurrentIdx(0);
              populateForm(records[0]);
            }
          }}
          autoFillDisabled={records.length === 0}
          autoFillReason="安全載入第一張既有物性檢驗報告，不會新增、修改或送審。"
          screenDraft={{
            mode,
            master: form.getFieldsValue(),
            details,
          }}
        />
      </Card>

      {/* Approval History Modal */}
      {current && (
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={current.fp230_01}
          onClose={() => setShowHistory(false)}
        />
      )}
    </Spin>
  );
};

export default P203Form;
